import { css } from 'aphrodite';
import googleLogo from '../../assets/GoogleIcon.png';
import { Button } from '../../components/Button';
import { baseStyleSheet } from '../../styles/styles';
import { StarRating } from '../surveys/StarRating';
import { CompactLogoIcon } from '../svgs/icons/CompactLogoIcon';
import { DEFAULT_COLORS } from './constants';
import { styleSheet } from './styles';
import { TestimonialPreviewProps } from './types';

export const TestimonialPreview = ({
	bgColor,
	textColor,
	reviewText,
	authorName,
	rating,
	selectedBackgroundImage,
	includeGoogleLogo,
	includeAuthor,
	includeStars,
	sourceType,
	isCreatingDraft,
	errorMessage,
	previewRef,
	imageGenerationRef,
	onCreateDraft,
}: TestimonialPreviewProps) => {
	const backgroundImageUrl = selectedBackgroundImage?.fileAttachment?.url ?? null;

	return (
		<div className={css(styleSheet.previewPane)}>
			<div className={css(styleSheet.contentContainer)}>
				<h3 className={css(styleSheet.subtitle)}>Preview</h3>
				<span className={css(styleSheet.description)}>Review how your custom testimonial will look</span>

				<div className={css(styleSheet.previewWrapper)}>
					<div className={css(styleSheet.previewContainer)}>
						<div
							ref={previewRef}
							className={css(styleSheet.reviewCard)}
							style={{
								backgroundColor: bgColor,
								backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
								backgroundSize: backgroundImageUrl ? 'cover' : undefined,
								backgroundPosition: backgroundImageUrl ? 'top left' : undefined,
								backgroundRepeat: backgroundImageUrl ? 'no-repeat' : undefined,
								color: textColor,
							}}
						>
							<div>
								<div className={css(styleSheet.logoContainer)}>
									{sourceType === 'google_review' && includeGoogleLogo && (
										<img src={googleLogo} alt='Google Logo' className={css(styleSheet.logo)} />
									)}
									{sourceType === 'survey_response' && <CompactLogoIcon height={72} width={72} />}

									{rating > 0 && includeStars && (
										<div className={css(styleSheet.ratingContainer)}>
											<StarRating
												total={5}
												value={rating}
												readonly
												fillColor={DEFAULT_COLORS.star}
												starStyles={[styleSheet.ratingStar]}
											/>
										</div>
									)}
								</div>

								<div className={css(styleSheet.reviewText)}>
									<div className={css(styleSheet.reviewTextContent)}>{`"${reviewText}"`}</div>
								</div>
							</div>

							{includeAuthor && (
								<div className={css(styleSheet.authorContainer)}>
									<span className={css(styleSheet.authorText)}>— {authorName || 'Author Name'}</span>
								</div>
							)}
						</div>
					</div>
				</div>

				{isCreatingDraft && (
					<div className={css(styleSheet.successIndicator)}>
						<p className={css(styleSheet.successMessage)}>Creating draft post...</p>
					</div>
				)}

				{errorMessage && (
					<div className={css(styleSheet.errorIndicator)}>
						<p className={css(styleSheet.errorMessage)}>{errorMessage}</p>
						<Button
							onClick={() => onCreateDraft(null)}
							className={css(baseStyleSheet.ctaButton)}
							disabled={isCreatingDraft}
						>
							Continue without Image
						</Button>
					</div>
				)}

				{/* Hidden container for image generation with fixed dimensions */}
				<div className={css(styleSheet.hiddenImageContainer)}>
					<div
						ref={imageGenerationRef}
						className={css(styleSheet.imageGenerationCard)}
						style={{
							backgroundColor: bgColor,
							backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
							backgroundSize: backgroundImageUrl ? 'cover' : undefined,
							backgroundPosition: backgroundImageUrl ? 'center' : undefined,
							backgroundRepeat: backgroundImageUrl ? 'no-repeat' : undefined,
							color: textColor,
							fontFamily: 'Sora',
						}}
					>
						<div>
							<div className={css(styleSheet.imageGenerationLogoContainer)}>
								{sourceType === 'google_review' && includeGoogleLogo && (
									<img src={googleLogo} alt='Google Logo' className={css(styleSheet.imageGenerationLogo)} />
								)}
								{sourceType === 'survey_response' && <CompactLogoIcon height={72} width={72} />}

								{rating > 0 && includeStars && (
									<div className={css(styleSheet.ratingContainer)}>
										<StarRating
											total={5}
											value={rating}
											readonly
											fillColor={DEFAULT_COLORS.star}
											starStyles={[styleSheet.imageGenerationRatingStar]}
										/>
									</div>
								)}
							</div>

							<div className={css(styleSheet.imageGenerationReviewText)} style={{ color: textColor }}>
								<div
									className={css(styleSheet.reviewTextContent)}
									style={{ color: textColor }}
								>{`"${reviewText}"`}</div>
							</div>
						</div>

						{includeAuthor && (
							<div className={css(styleSheet.imageGenerationAuthorContainer)} style={{ color: textColor }}>
								<span className={css(styleSheet.imageGenerationAuthorText)} style={{ color: textColor }}>
									— {authorName || 'Author Name'}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
